/**
 * <QuestionsAndAnswers />
 *
 * Phase 16 — public Q&A on a product listing.
 *
 * Layout per Carlton's spec:
 *   * Header "Questions & Answers" + count badge
 *   * "Ask a question" CTA (auth-gated)
 *   * Q/A list, newest-first
 *   * Seller controls inline (answer / edit answer)
 *   * Admin controls inline (hide)
 *   * "Load more" pagination
 *
 * Anonymous viewers see the list. The Ask CTA reroutes to
 * `/login?redirect=…` so the convention matches the rest of the app
 * (Login.tsx reads `?redirect=`).
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle, Loader2, MessageSquare, ShieldOff } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import type { ProductQuestion } from "../../lib/types";
import { Button } from "../ui/Button";

interface Props {
  productId: string;
  sellerId: string;
}

const PAGE_SIZE = 20;

export function QuestionsAndAnswers({ productId, sellerId }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSeller = !!user && user.id === sellerId;
  const isAdmin = !!user && user.role === "ADMIN";

  const [items, setItems] = React.useState<ProductQuestion[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [hasMore, setHasMore] = React.useState(false);
  const [offset, setOffset] = React.useState(0);

  // Per-question UI flags. Stored as id → mode so multiple rows can
  // have their inline forms open simultaneously.
  const [answeringId, setAnsweringId] = React.useState<string | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);

  // Ask-a-question composer state.
  const [askOpen, setAskOpen] = React.useState(false);
  const [askText, setAskText] = React.useState("");
  const [askBusy, setAskBusy] = React.useState(false);
  const [askError, setAskError] = React.useState<string | null>(null);

  const load = React.useCallback(
    async (nextOffset: number, append: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get<ProductQuestion[]>(
          `/products/${productId}/questions`,
          { params: { limit: PAGE_SIZE, offset: nextOffset } },
        );
        setItems((prev) => (append ? [...prev, ...r.data] : r.data));
        setHasMore(r.data.length === PAGE_SIZE);
        setOffset(nextOffset + r.data.length);
      } catch (err) {
        setError(extractApiError(err));
      } finally {
        setLoading(false);
      }
    },
    [productId],
  );

  React.useEffect(() => {
    setItems([]);
    setOffset(0);
    setHasMore(false);
    load(0, false);
  }, [load]);

  // -----------------------------------------------------------------
  // Ask
  // -----------------------------------------------------------------
  const onAskClick = () => {
    if (!user) {
      navigate(`/login?redirect=/product/${productId}#questions`);
      return;
    }
    setAskOpen((v) => !v);
  };

  const submitAsk = async (e: React.FormEvent) => {
    e.preventDefault();
    setAskError(null);
    setAskBusy(true);
    try {
      const r = await api.post<ProductQuestion>(
        `/products/${productId}/questions`,
        { question_text: askText },
      );
      // Prepend optimistically — backend orders newest-first too.
      setItems((prev) => [r.data, ...prev]);
      setAskText("");
      setAskOpen(false);
    } catch (err) {
      setAskError(extractApiError(err));
    } finally {
      setAskBusy(false);
    }
  };

  // -----------------------------------------------------------------
  // Answer / edit
  // -----------------------------------------------------------------
  const updateRow = (q: ProductQuestion) => {
    setItems((prev) => prev.map((x) => (x.id === q.id ? q : x)));
  };

  // -----------------------------------------------------------------
  // Hide (admin)
  // -----------------------------------------------------------------
  const onHide = async (qid: string) => {
    try {
      await api.delete(`/admin/products/questions/${qid}`);
      setItems((prev) => prev.filter((x) => x.id !== qid));
    } catch (err) {
      setError(extractApiError(err));
    }
  };

  return (
    <section
      id="questions"
      data-testid="product-qa"
      className="mt-8"
    >
      <header className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xl md:text-2xl font-black inline-flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-brand" aria-hidden />
          Questions & Answers
          <span
            className="inline-flex items-center justify-center min-w-[26px] h-[22px] rounded-full bg-ink text-white text-xs font-bold px-2"
            data-testid="qa-count"
          >
            {items.length}
          </span>
        </h2>
      </header>

      {/* Ask CTA */}
      {isSeller ? (
        <p
          className="text-sm text-ink-muted italic mb-4"
          data-testid="qa-seller-hint"
        >
          Answer questions buyers post here.
        </p>
      ) : (
        <div className="mb-4">
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={onAskClick}
            leadingIcon={<HelpCircle className="h-4 w-4" />}
            data-testid="qa-ask-btn"
          >
            {user ? "Ask a question" : "Sign in to ask a question"}
          </Button>
          {askOpen && user && (
            <form
              onSubmit={submitAsk}
              className="mt-3 rounded-xl border-2 border-surface-border bg-surface-raised p-4"
              data-testid="qa-ask-form"
            >
              <label
                htmlFor="qa-ask-textarea"
                className="block text-sm font-semibold text-ink mb-1.5"
              >
                Your question
              </label>
              <textarea
                id="qa-ask-textarea"
                value={askText}
                onChange={(e) => setAskText(e.target.value.slice(0, 500))}
                minLength={10}
                maxLength={500}
                rows={4}
                required
                placeholder="e.g. Does this fit a 2003 Bayliner 175?"
                className="w-full rounded-xl border-2 border-surface-border bg-white px-3 py-3 text-base placeholder:text-ink-subtle focus:outline-none focus:border-ink"
                data-testid="qa-ask-textarea"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-ink-muted">
                  {askText.trim().length}/500 characters · 10 minimum
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAskOpen(false);
                      setAskText("");
                      setAskError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    loading={askBusy}
                    disabled={askText.trim().length < 10}
                    data-testid="qa-ask-submit"
                  >
                    Post question
                  </Button>
                </div>
              </div>
              {askError && (
                <p
                  className="mt-2 text-sm font-semibold text-brand"
                  data-testid="qa-ask-error"
                >
                  {askError}
                </p>
              )}
            </form>
          )}
        </div>
      )}

      {/* List */}
      {error && (
        <p
          className="text-sm font-semibold text-brand mb-3"
          data-testid="qa-error"
        >
          {error}
        </p>
      )}

      {loading && items.length === 0 ? (
        <div className="py-8 text-center text-ink-muted">
          <Loader2 className="h-6 w-6 animate-spin inline-block" aria-hidden />
        </div>
      ) : items.length === 0 ? (
        <p
          className="text-ink-muted text-sm italic"
          data-testid="qa-empty"
        >
          No questions yet. Be the first to ask.
        </p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="qa-list">
          {items.map((q) => (
            <QARow
              key={q.id}
              q={q}
              productId={productId}
              isSeller={isSeller}
              isAdmin={isAdmin}
              answering={answeringId === q.id}
              editing={editingId === q.id}
              onStartAnswer={() => {
                setAnsweringId(q.id);
                setEditingId(null);
              }}
              onStartEdit={() => {
                setEditingId(q.id);
                setAnsweringId(null);
              }}
              onCancel={() => {
                setAnsweringId(null);
                setEditingId(null);
              }}
              onUpdated={(updated) => {
                updateRow(updated);
                setAnsweringId(null);
                setEditingId(null);
              }}
              onHide={onHide}
            />
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="mt-4 text-center">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={() => load(offset, true)}
            loading={loading}
            data-testid="qa-load-more"
          >
            Load more
          </Button>
        </div>
      )}
    </section>
  );
}

// =====================================================================
// QARow — single Q/A pair + inline answer/edit/hide controls
// =====================================================================
function QARow({
  q,
  productId,
  isSeller,
  isAdmin,
  answering,
  editing,
  onStartAnswer,
  onStartEdit,
  onCancel,
  onUpdated,
  onHide,
}: {
  q: ProductQuestion;
  productId: string;
  isSeller: boolean;
  isAdmin: boolean;
  answering: boolean;
  editing: boolean;
  onStartAnswer: () => void;
  onStartEdit: () => void;
  onCancel: () => void;
  onUpdated: (q: ProductQuestion) => void;
  onHide: (qid: string) => void;
}) {
  const hasAnswer = !!q.answer_text;

  return (
    <li
      className="rounded-2xl border border-surface-border bg-white p-4"
      data-testid={`qa-row-${q.id}`}
    >
      {/* Question */}
      <div className="flex items-start gap-2">
        <span className="font-display font-black text-brand">Q:</span>
        <div className="flex-1 min-w-0">
          <p className="text-ink leading-relaxed whitespace-pre-line">
            {q.question_text}
          </p>
          <p className="text-xs text-ink-muted mt-1">
            asked by{" "}
            <strong className="text-ink-soft">@{q.asker_username}</strong> ·{" "}
            {relativeTime(q.asked_at)}
          </p>
        </div>
      </div>

      {/* Answer (if present, and we're not currently editing) */}
      {hasAnswer && !editing && (
        <div className="mt-3 ml-2 pl-3 border-l-4 border-ink/20">
          <div className="flex items-start gap-2">
            <span className="font-display font-black text-ink">A:</span>
            <div className="flex-1 min-w-0">
              <p
                className="text-ink leading-relaxed whitespace-pre-line"
                data-testid={`qa-answer-text-${q.id}`}
              >
                {q.answer_text}
              </p>
              <p className="text-xs text-ink-muted mt-1">
                answered by{" "}
                <strong className="text-ink-soft">
                  @{q.answerer_username ?? "seller"}
                </strong>
                {q.answered_at && <> · {relativeTime(q.answered_at)}</>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Unanswered marker — seller + admin only */}
      {!hasAnswer && (isSeller || isAdmin) && !answering && (
        <p
          className="mt-3 ml-2 text-xs italic text-ink-muted"
          data-testid={`qa-no-answer-${q.id}`}
        >
          [No answer yet]
        </p>
      )}

      {/* Inline answer / edit form */}
      {(answering || editing) && (
        <AnswerForm
          productId={productId}
          q={q}
          mode={editing ? "edit" : "create"}
          onCancel={onCancel}
          onUpdated={onUpdated}
        />
      )}

      {/* Action row */}
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        {isSeller && !hasAnswer && !answering && (
          <button
            type="button"
            onClick={onStartAnswer}
            className="text-sm font-semibold text-brand hover:text-brand-hover"
            data-testid={`qa-answer-btn-${q.id}`}
          >
            Answer
          </button>
        )}
        {isSeller && hasAnswer && !editing && (
          <button
            type="button"
            onClick={onStartEdit}
            className="text-sm font-semibold text-ink-muted hover:text-ink"
            data-testid={`qa-edit-btn-${q.id}`}
          >
            Edit answer
          </button>
        )}
        {isAdmin && (
          <button
            type="button"
            onClick={() => onHide(q.id)}
            className="text-sm font-semibold text-ink-muted hover:text-brand inline-flex items-center gap-1"
            data-testid={`qa-hide-btn-${q.id}`}
          >
            <ShieldOff className="h-3.5 w-3.5" aria-hidden /> Hide
          </button>
        )}
      </div>
    </li>
  );
}

// =====================================================================
// AnswerForm — POST or PATCH the answer
// =====================================================================
function AnswerForm({
  productId,
  q,
  mode,
  onCancel,
  onUpdated,
}: {
  productId: string;
  q: ProductQuestion;
  mode: "create" | "edit";
  onCancel: () => void;
  onUpdated: (q: ProductQuestion) => void;
}) {
  const [text, setText] = React.useState(
    mode === "edit" ? q.answer_text ?? "" : "",
  );
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const url = `/products/${productId}/questions/${q.id}/answer`;
      const body = { answer_text: text };
      const r =
        mode === "edit"
          ? await api.patch<ProductQuestion>(url, body)
          : await api.post<ProductQuestion>(url, body);
      onUpdated(r.data);
    } catch (e2) {
      setErr(extractApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mt-3 ml-2 pl-3 border-l-4 border-brand"
      data-testid={`qa-answer-form-${q.id}`}
    >
      <label
        htmlFor={`qa-answer-textarea-${q.id}`}
        className="block text-sm font-semibold text-ink mb-1.5"
      >
        {mode === "edit" ? "Edit your answer" : "Your answer"}
      </label>
      <textarea
        id={`qa-answer-textarea-${q.id}`}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 1000))}
        minLength={5}
        maxLength={1000}
        rows={3}
        required
        className="w-full rounded-xl border-2 border-surface-border bg-white px-3 py-3 text-base placeholder:text-ink-subtle focus:outline-none focus:border-ink"
        data-testid={`qa-answer-textarea-${q.id}`}
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-ink-muted">
          {text.trim().length}/1000 characters · 5 minimum
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={busy}
            disabled={text.trim().length < 5}
            data-testid={`qa-answer-submit-${q.id}`}
          >
            {mode === "edit" ? "Save changes" : "Post answer"}
          </Button>
        </div>
      </div>
      {err && (
        <p
          className="mt-2 text-sm font-semibold text-brand"
          data-testid={`qa-answer-error-${q.id}`}
        >
          {err}
        </p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------
function relativeTime(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}
